import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { CloudUpload, Delete, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createBanner, getBannerById, updateBanner, getAllServiceLocations } from '../../services/Api';

const ModifyBanner = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);

  const [formData, setFormData] = useState({
    position: 'top',
    isActive: true,
    locationIds: [],
  });
  const [serviceLocations, setServiceLocations] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [existingImage, setExistingImage] = useState(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const locRes = await getAllServiceLocations(1, 100);
        if (locRes.success) {
          setServiceLocations(Array.isArray(locRes.data) ? locRes.data : (locRes.data?.docs || []));
        }
      } catch (err) {
        console.error("Failed to fetch locations", err);
      }
    };
    fetchInitialData();

    // 1. Try to get data from navigation state (most efficient)
    if (location.state?.banner) {
      const { banner } = location.state;

      let parsedLocationIds = [];
      if (banner.locations && Array.isArray(banner.locations)) {
        parsedLocationIds = banner.locations.map(loc => typeof loc === 'object' ? (loc.id || loc._id) : loc);
      } else if (banner.locationIds) {
        parsedLocationIds = Array.isArray(banner.locationIds)
          ? banner.locationIds
          : banner.locationIds.split(',').filter(Boolean);
      }

      setFormData({
        position: banner.position || 'top',
        isActive: banner.isActive !== false,
        locationIds: parsedLocationIds,
      });
      setExistingImage(banner.image);
      if (banner.image) {
        setPreviews([banner.image]);
      }
      return;
    }

    // 2. If no state (direct link), fetch from API
    if (id) {
      const fetchBanner = async () => {
        setLoading(true);
        try {
          const response = await getBannerById(id);
          if (response.success) {

            let parsedLocationIds = [];
            if (response.data.locations && Array.isArray(response.data.locations)) {
              parsedLocationIds = response.data.locations.map(loc => typeof loc === 'object' ? (loc.id || loc._id) : loc);
            } else if (response.data.locationIds) {
              parsedLocationIds = Array.isArray(response.data.locationIds)
                ? response.data.locationIds
                : response.data.locationIds.split(',').filter(Boolean);
            }

            setFormData({
              position: response.data.position || 'top',
              isActive: response.data.isActive !== false,
              locationIds: parsedLocationIds,
            });
            setExistingImage(response.data.image);
            if (response.data.image) {
              setPreviews([response.data.image]);
            }
          } else {
            setError(response?.message || 'Failed to fetch banner details (404). Data was loaded from list view instead.');
          }
        } catch (err) {
          setError(err.response?.data?.message || err.message || 'An error occurred while fetching banner details');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchBanner();
    }
  }, [id, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: typeof value === 'string' && name === 'locationIds' ? value.split(',') : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFiles([file]);
      setPreviews([URL.createObjectURL(file)]);
      setExistingImage(null); // Clear existing if a new file is selected
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    if (index === 0 && existingImage) setExistingImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0 && !existingImage) {
      setError('Please select at least one banner image.');
      return;
    }

    if (!id && !formData.isActive) {
      setError('Status should be active when creating a new banner.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    const submitData = new FormData();
    selectedFiles.forEach((file) => {
      submitData.append('bannerImages', file);
    });
    submitData.append('position', formData.position);
    submitData.append('isActive', formData.isActive);
    if (formData.locationIds && formData.locationIds.length > 0) {
      formData.locationIds
        .filter(id => id != null && id !== 'undefined' && id !== 'null')
        .forEach(id => submitData.append('locationIds[]', id));
    }

    try {
      let response;
      if (id) {
        response = await updateBanner(id, submitData);
      } else {
        response = await createBanner(submitData);
      }

      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate('/banners'), 1500);
      } else {
        setError(response.message || `Failed to ${id ? 'update' : 'create'} banner`);
      }
    } catch (err) {
      setError(err.response?.data?.message || `An error occurred while ${id ? 'updating' : 'creating'} the banner`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      {loading && !formData.position && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/banners');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
            Banners
          </Link>
          <Typography color="text.primary">{id ? 'Edit Banner' : 'Create New Banner'}</Typography>
        </Breadcrumbs>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {id ? 'Edit Banner' : 'Create Banner'}
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>Banner created successfully! Redirecting...</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4} alignItems={"center"}>
            {/* Image Upload Section */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Banner Image *
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: 250,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  position: 'relative'
                }}
              >
                {previews.length > 0 ? (
                  <>
                    <Box
                      component="img"
                      src={previews[0]}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': { backgroundColor: 'white' },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(0);
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1">Upload Banner Image</Typography>
                  </>
                )}
                <input
                  type="file"
                  id="banner-upload"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    handleFileChange(e);
                    e.target.value = null;
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                Note: Image size must be 600px * 300px (or 300px * 300px for Popup) based on position
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  sx={{ mt: 2, fontSize: "10px !important" }}
                  startIcon={<CloudUpload />}
                  onClick={() => document.getElementById("banner-upload").click()}
                >
                  Upload Image
                </Button>
              </Box>
            </Grid>

            {/* Config Section */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <FormControl size="small" fullWidth variant="outlined">
                      <InputLabel id="position-label">Position where the banner will be displayed</InputLabel>
                      <Select
                        labelId="position-label"
                        id="position"
                        value={formData.position}
                        label="Position where the banner will be displayed"
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      >
                        <MenuItem value="top">Top</MenuItem>
                        <MenuItem value="middle">Middle</MenuItem>
                        <MenuItem value="bottom">Bottom</MenuItem>
                        <MenuItem value="popup">Popup</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <FormControl size="small" fullWidth variant="outlined">
                      <InputLabel id="isActive-label">Whether the banner is active</InputLabel>
                      <Select
                        labelId="isActive-label"
                        id="isActive"
                        value={formData.isActive}
                        label="Whether the banner is active"
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                      >
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <FormControl size="small" fullWidth  >
                      <InputLabel id="location-select-label" size="small">Service Locations</InputLabel>
                      <Select
                        labelId="location-select-label"
                        name="locationIds"
                        multiple
                        open={isLocationMenuOpen}
                        onOpen={() => setIsLocationMenuOpen(true)}
                        onClose={() => setIsLocationMenuOpen(false)}
                        value={formData.locationIds}
                        onChange={handleInputChange}
                        input={<OutlinedInput label="Service Locations" />}
                        renderValue={(selected) => selected.map(id => serviceLocations.find(l => (l.id || l._id) === id)?.name || id).join(', ')}
                      >
                        {serviceLocations.map((loc) => (
                          <MenuItem key={loc.id || loc._id} value={loc.id || loc._id}>
                            <Checkbox size='small' checked={formData.locationIds.indexOf(loc.id || loc._id) > -1} />
                            <ListItemText primary={loc.name} />
                          </MenuItem>
                        ))}
                        <MenuItem 
                          disableRipple 
                          sx={{ display: 'flex', justifyContent: 'flex-end', '&:hover': { backgroundColor: 'transparent' }, cursor: 'default' }}
                          onClick={(e) => {
                             e.stopPropagation();
                             setIsLocationMenuOpen(false);
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            size='small'
                            sx={{ mt: 1}}
                          >
                            Done
                          </Button>
                        </MenuItem>
                      </Select>
                      
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/banners')}
                    disabled={loading}
                    sx={{ py: 1.5 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      backgroundColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      py: 1.5,
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : (id ? 'Update Banner' : 'Create Banner')}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default ModifyBanner;
